// DayScrubber + DayProfile - the building's operating clock, from the
// energy model's schedules. The scrubber owns {dayType, hour}; the
// profile draws the 24 hours with the current hour boxed.
import React from "react";
import { DAY_LABEL, fmtHour, profileAt } from "./glanceModel";

export function scheduleRows(manifest) {
  const ops = manifest.operations;
  if (!ops) return [];
  const rows = [];
  if (ops.profiles.occupancy) rows.push({ key: "occupancy", label: "Occupancy", kind: "area", color: "var(--occ)" });
  if (ops.profiles.lighting) rows.push({ key: "lighting", label: "Lighting", kind: "area", color: "var(--lite)" });
  const groups = {};
  ((manifest.roof && manifest.roof.equipment) || []).forEach((e) => {
    if (!e.schedule || e.schedule === "always" || !ops.profiles[e.schedule]) return;
    (groups[e.schedule] = groups[e.schedule] || []).push(e);
  });
  Object.entries(groups).forEach(([key, units]) => {
    const rtu = units.every((u) => u.kind === "rtu");
    rows.push({ key, label: rtu ? "Rooftop units" : units.map((u) => u.id).join(", "), kind: "band", color: "var(--occ)", units });
  });
  (ops.alwaysOn || []).forEach((a, i) => {
    const sys = (manifest.systems || []).find((s) => s.id === a.system);
    rows.push({ key: `always-${i}`, label: `${sys ? sys.label.replace(/ rooms?$/i, "") : a.system} cooling`, kind: "always", note: a.label });
  });
  return rows;
}

export function statusSentence(manifest, dayType, hour) {
  const ops = manifest.operations;
  const bits = [];
  const occ = profileAt(ops, "occupancy", dayType, hour);
  const lit = profileAt(ops, "lighting", dayType, hour);
  if (occ != null) bits.push(`${occ}% occupancy`);
  if (lit != null) bits.push(`${lit}% lighting`);
  let s = `On a ${DAY_LABEL[dayType].toLowerCase()} at ${fmtHour(hour)}`;
  s += bits.length ? `, the model assumes ${bits.join(" and ")}.` : ".";
  scheduleRows(manifest).forEach((r) => {
    if (r.kind === "band") s += ` ${r.label} are ${profileAt(ops, r.key, dayType, hour) ? "on" : "set back"}.`;
    if (r.kind === "always") s += ` ${r.label} is on.`;
  });
  return s;
}

export function DayControls({ dayTypes, dayType, hour, onChange, status }) {
  return (
    <div className="g-ctrl">
      <div className="g-seg" role="group" aria-label="Day type">
        {dayTypes.map((d) => (
          <button key={d} type="button" aria-pressed={d === dayType} onClick={() => onChange({ dayType: d, hour })}>{DAY_LABEL[d] || d}</button>
        ))}
      </div>
      <span className="g-clock">{fmtHour(hour)}</span>
      <label htmlFor="g-hour" className="g-sr">Hour of day</label>
      <input id="g-hour" type="range" min="0" max="23" step="1" value={hour} aria-valuetext={fmtHour(hour)}
             onChange={(e) => onChange({ dayType, hour: Number(e.target.value) })} />
      <div className="g-status" aria-live="polite">{status}</div>
    </div>
  );
}

export function DayProfile({ manifest, dayType, hour }) {
  const rows = scheduleRows(manifest);
  if (!rows.length) return null;
  const X0 = 170; const W = 930;
  const cx = (h) => X0 + (h * W) / 24;
  const layout = [];
  let y = 8;
  rows.forEach((r) => { const h = r.kind === "area" ? 42 : 36; layout.push({ ...r, y, h }); y += h; });
  const bottom = y + 6;
  const ops = manifest.operations;
  return (
    <svg viewBox={`0 0 1140 ${bottom + 26}`} role="img" aria-label="24-hour operating profiles">
      {[0, 6, 12, 18].map((h) => (
        <g key={h}>
          <line x1={cx(h)} y1={4} x2={cx(h)} y2={bottom} stroke="var(--line)" strokeWidth="0.5" />
          <text x={cx(h)} y={bottom + 18} textAnchor="middle" fontSize="14" fill="var(--ink2)">{h === 0 ? "12 AM" : h === 12 ? "12 PM" : h < 12 ? `${h} AM` : `${h - 12} PM`}</text>
        </g>
      ))}
      {layout.map((r) => (
        <g key={r.key}>
          <text x={X0 - 12} y={r.y + r.h / 2 + 5} textAnchor="end" fontSize="15" fill="var(--ink2)">{r.label}</text>
          {r.kind === "area" && (() => {
            const base = r.y + r.h - 4; const k = (r.h - 8) / 100;
            let d = `M${cx(0)} ${base}`;
            for (let h = 0; h < 24; h++) { const v = profileAt(ops, r.key, dayType, h) || 0; d += ` L${cx(h)} ${base - v * k} L${cx(h + 1)} ${base - v * k}`; }
            d += ` L${cx(24)} ${base} Z`;
            return <path d={d} fill={r.color} fillOpacity="0.35" stroke={r.color} strokeWidth="1.5" />;
          })()}
          {r.kind === "band" && Array.from({ length: 24 }, (_, h) => h).filter((h) => profileAt(ops, r.key, dayType, h)).map((h) => (
            <rect key={h} x={cx(h)} y={r.y + 8} width={W / 24} height={r.h - 16} fill="var(--occ)" fillOpacity="0.55" />
          ))}
          {r.kind === "always" && (
            <>
              <rect x={cx(0)} y={r.y + 8} width={W} height={r.h - 16} fill="var(--system)" />
              <text x={cx(0) + 8} y={r.y + r.h / 2 + 5} fontSize="14" fill="var(--systemink)">{r.note}</text>
            </>
          )}
        </g>
      ))}
      <rect x={cx(hour)} y={2} width={W / 24} height={bottom - 2} fill="none" stroke="var(--riser)" strokeWidth="2" />
    </svg>
  );
}
